import { PlusSmIcon } from "@heroicons/react/outline";
import { ArrowsExpandIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import VideoTile from "./VideoLayoutTile";

export function VideoTag(props) {
  const video = useRef();
  const srcObject = props.srcObject;
  const src = props.src;
  const sharingScreen = props.sharingScreen;
  const [isFullScreen, setIsFullScreen] = useState(false);

  const className = classNames(
    "static shadow-lg bg-slate-900 max-w-full max-h-full",
    props.className,
    sharingScreen ? "" : "object-cover"
  );
  function handleCanPlay() {
    video.current.play();
  }

  function requestFullScreen() {
    if (!isFullScreen) {
      video.current.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  }

  useEffect(() => {
    if (srcObject && video.current) {
      video.current.srcObject = srcObject;
    }
  });

  return (
    <>
      <video
        ref={video}
        onCanPlay={handleCanPlay}
        playsInline
        className={className}
        autoPlay={true}
        src={src}
      />
      {sharingScreen ? (
        <button
          onClick={requestFullScreen}
          type="button"
          className="absolute top-1 left-0 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <ArrowsExpandIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : (
        ""
      )}
    </>
  );
}

function VideoLayoutGrid(props) {
  const ownerOnlyBroadcast = props.ownerOnlyBroadcast;
  const meeting = props.meeting;
  const mockVideoTags = props.mockVideoTags;
  const onlineUsers = props.onlineUsers;
  const localMediaStream = props.localMediaStream;
  const videoTiles = props.videoTiles;
  const [tiles, setTiles] = useState([]);
  const activeSpeakerId = props.activeSpeakerId;

  let videoElementsCount = onlineUsers.length - 1;
  let isSharingScreen = false;
  let selfSharingScreen = false;
  let screenShareCount = 0;
  let anyDeviceSharingCount = 0;
  /**
   * Checking if any user is sharing screen
   */

  for (let user of onlineUsers) {
    if (user.sharingScreen) {
      isSharingScreen = true;
      screenShareCount++;
      if (user._id === meeting.participantSessionId) {
        selfSharingScreen = true;
      }
    }

    if (user.shareAudio || user.sharingVideo || user.sharingScreen) {
      anyDeviceSharingCount++;
    }
  }

  if (isSharingScreen) {
    videoElementsCount = screenShareCount;
    if (selfSharingScreen) {
      videoElementsCount = videoElementsCount - 1;
    }
  }

  if (ownerOnlyBroadcast) {
    videoElementsCount = anyDeviceSharingCount;
  }

  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth
  });

  useLayoutEffect(() => {
    function handleResize() {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  let isMobile = false;
  if (dimensions.width < 960) {
    isMobile = true;
  }

  let gridClasses = "";
  if (isMobile) {
    if (videoElementsCount === 1) {
      gridClasses = "grid-cols-1";
    }

    if (videoElementsCount === 2) {
      gridClasses = "grid-rows-2";
    }

    if (videoElementsCount >= 3 && videoElementsCount <= 6) {
      gridClasses = "grid-rows-3";
    }

    if (videoElementsCount >= 7 && videoElementsCount <= 8) {
      gridClasses = "grid-rows-4";
    }

    if (videoElementsCount > 8) {
      gridClasses = "grid-rows-5";
    }
  } else {
    if (videoElementsCount === 1) {
      gridClasses = "grid-cols-1";
    }

    if (videoElementsCount >= 2 && videoElementsCount <= 4) {
      gridClasses = "grid-cols-2";
    }

    if (videoElementsCount >= 5 && videoElementsCount <= 6) {
      gridClasses = "grid-rows-2";
    }

    if (videoElementsCount >= 7 && videoElementsCount <= 9) {
      gridClasses = "grid-cols-2";
    }

    if (videoElementsCount >= 10 && videoElementsCount <= 12) {
      gridClasses = "grid-rows-3";
    }

    if (videoElementsCount > 12) {
      gridClasses = "grid-rows-4";
    }
  }

  useLayoutEffect(() => {
    console.log("VideoLayoutGrid - Render Called");
    let renderTiles = [];
    let isMobile = false;
    if (dimensions.width < 960) {
      isMobile = true;
    }

    if (isMobile) {
      if (videoElementsCount <= 3) {
        for (let user of onlineUsers) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }

          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          renderTiles.push(
            <VideoTile
              key={user._id}
              userId={user._id}
              activeSpeakerId={activeSpeakerId}
              drawUserNameBox={drawUserNameBox}
              username={user.name}
              videoTags={videoTags}
              hidden={hidden}
            />
          );
        }
      }

      if (videoElementsCount > 3 && videoElementsCount <= 6) {
        let topRow = [];
        let middleRow = [];
        let bottomRow = [];

        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }

          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (counter === 0 || counter === 1) {
            topRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (
            (videoElementsCount === 4 && counter === 2) ||
            ((videoElementsCount === 5 || videoElementsCount === 6) &&
              (counter === 2 || counter === 3))
          ) {
            middleRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (
            (videoElementsCount === 4 && counter === 3) ||
            (videoElementsCount === 5 && counter === 4) ||
            (videoElementsCount === 6 && (counter === 4 || counter === 5))
          ) {
            bottomRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(<div className={classNames("grid grid-cols-2")}>{topRow}</div>);

        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-1": videoElementsCount === 4,
              "grid-cols-2": videoElementsCount > 4
            })}
          >
            {middleRow}
          </div>
        );
        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-1": videoElementsCount <= 5,
              "grid-cols-2": videoElementsCount > 5
            })}
          >
            {bottomRow}
          </div>
        );
      }

      if (videoElementsCount > 6) {
        let firstRow = [];
        let secondRow = [];
        let thirdRow = [];
        let forthRow = [];
        let fifthRow = [];

        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (counter === 0 || counter === 1) {
            firstRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (counter === 2 || counter === 3) {
            secondRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (counter === 4 || counter === 5) {
            thirdRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (
            (videoElementsCount === 7 && counter === 6) ||
            (videoElementsCount > 7 && (counter === 6 || counter === 7))
          ) {
            forthRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (videoElementsCount > 8 && (counter === 8 || counter === 9)) {
            fifthRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(<div className={classNames("grid grid-cols-2")}>{firstRow}</div>);
        renderTiles.push(<div className={classNames("grid grid-cols-2")}>{secondRow}</div>);
        renderTiles.push(<div className={classNames("grid grid-cols-2")}>{thirdRow}</div>);
        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-1": videoElementsCount === 7,
              "grid-cols-2": videoElementsCount > 7
            })}
          >
            {forthRow}
          </div>
        );
        if (fifthRow.length > 0) {
          renderTiles.push(
            <div
              className={classNames("grid", {
                "grid-cols-1": videoElementsCount === 7,
                "grid-cols-2": videoElementsCount > 7
              })}
            >
              {fifthRow}
            </div>
          );
        }
      }
    } else {
      if (videoElementsCount <= 2) {
        for (let user of onlineUsers) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }

          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }

          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          renderTiles.push(
            <VideoTile
              key={user._id}
              userId={user._id}
              activeSpeakerId={activeSpeakerId}
              drawUserNameBox={drawUserNameBox}
              username={user.name}
              videoTags={videoTags}
              hidden={hidden}
            />
          );
        }
      }

      if (videoElementsCount === 3 || videoElementsCount === 4) {
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          // let lastItem = false;
          // if (videoElementsCount === 3 && index === onlineUsers.length) {
          //   lastItem = true;
          // }

          renderTiles.push(
            <VideoTile
              key={user._id}
              userId={user._id}
              activeSpeakerId={activeSpeakerId}
              drawUserNameBox={drawUserNameBox}
              username={user.name}
              videoTags={videoTags}
              hidden={hidden}
            />
          );
        }
      }

      if (videoElementsCount === 5 || videoElementsCount === 6) {
        const topRow = [];
        const bottomRow = [];

        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (counter <= 2) {
            topRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }

          if (counter > 2) {
            bottomRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(<div className="grid grid-cols-3">{topRow}</div>);

        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-2": videoElementsCount === 5,
              "grid-cols-3": videoElementsCount === 6
            })}
          >
            {bottomRow}
          </div>
        );
      }

      if (videoElementsCount === 7 || videoElementsCount === 8 || videoElementsCount === 9) {
        const leftCol = [];
        const rightCol = [];

        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (
            (videoElementsCount === 7 && counter === 0) ||
            (videoElementsCount === 8 && counter <= 1) ||
            (videoElementsCount === 9 && counter <= 2)
          ) {
            leftCol.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (counter > 0) {
            rightCol.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-1": videoElementsCount === 7,
              "grid-rows-2": videoElementsCount === 8,
              "grid-rows-3": videoElementsCount === 9
            })}
          >
            {leftCol}
          </div>
        );

        renderTiles.push(<div className="grid grid-cols-2 grid-rows-3">{rightCol}</div>);
      }

      if (videoElementsCount === 10 || videoElementsCount === 11 || videoElementsCount === 12) {
        const topRow = [];
        const middleRow = [];
        const bottomRow = [];
        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (counter >= 0 && counter < 4) {
            topRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (
            (videoElementsCount === 10 && counter >= 4 && counter < 7) ||
            (videoElementsCount > 10 && counter >= 4 && counter < 8)
          ) {
            middleRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (
            (videoElementsCount <= 11 && counter >= 7) ||
            (videoElementsCount > 11 && counter >= 8)
          ) {
            bottomRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(<div className="grid grid-cols-4">{topRow}</div>);
        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-3": videoElementsCount === 10,
              "grid-cols-4": videoElementsCount > 10
            })}
          >
            {middleRow}
          </div>
        );
        renderTiles.push(
          <div
            className={classNames("grid", {
              "grid-cols-3": videoElementsCount <= 11,
              "grid-cols-4": videoElementsCount > 11
            })}
          >
            {bottomRow}
          </div>
        );
      }

      if (videoElementsCount > 12) {
        const firstRow = [];
        const secondRow = [];
        const thirdRow = [];
        const forthRow = [];
        let counter = 0;
        for (let [index, user] of onlineUsers.entries()) {
          if (user._id === meeting.participantSessionId) {
            if (!ownerOnlyBroadcast) {
              continue;
            }
          }
          let hidden = false;
          if (isSharingScreen) {
            if (!user.sharingScreen) {
              hidden = true;
            }
          }
          const videoTags = [];
          let drawUserNameBox = true;
          if (videoTiles[user._id] && videoTiles[user._id].streams) {
            for (let tile of videoTiles[user._id].streams) {
              if (tile.type === "audio") {
                videoTags.push(
                  <VideoTag
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    className={"hidden"}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              } else {
                drawUserNameBox = false;
                videoTags.push(
                  <VideoTag
                    className={"h-full"}
                    key={tile.streamId}
                    sharingScreen={user.sharingScreen}
                    srcObject={new MediaStream([tile.track])}
                  />
                );
              }
            }
          } else {
            if (ownerOnlyBroadcast) {
              continue;
            }
          }

          if (counter >= 0 && counter < 4) {
            firstRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (counter >= 4 && counter < 8) {
            secondRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (counter >= 8 && counter < 12) {
            thirdRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          } else if (counter >= 12) {
            forthRow.push(
              <VideoTile
                key={user._id}
                userId={user._id}
                activeSpeakerId={activeSpeakerId}
                drawUserNameBox={drawUserNameBox}
                username={user.name}
                videoTags={videoTags}
                hidden={hidden}
              />
            );
          }
          counter++;
        }

        renderTiles.push(<div className="grid grid-cols-4">{firstRow}</div>);
        renderTiles.push(<div className="grid grid-cols-4">{secondRow}</div>);
        renderTiles.push(<div className="grid grid-cols-4">{thirdRow}</div>);
        renderTiles.push(<div className="grid grid-cols-4">{forthRow}</div>);
      }
    }

    setTiles(renderTiles);
  }, [JSON.stringify(onlineUsers), videoTiles, dimensions]);

  const localVideoTag = useMemo(() => {
    return <VideoTag className="w-24 h-24" srcObject={localMediaStream} />;
  }, [localMediaStream]);

  return (
    <div className={` w-full h-full grid relative ${gridClasses}`}>
      {tiles}

      {ownerOnlyBroadcast ? (
        <></>
      ) : (
        <div className="absolute sm:bottom-0 bottom-16 right-0 md:w-48 md:h-48 sm:h-24 sm:w-24">
          <div
            className={classNames(
              "bg-gray-600 flex items-center justify-center relative p-4 rounded-lg mr-2"
            )}
          >
            {!localMediaStream ? (
              <div className="text-white bg-yellow-500 w-20 h-20 rounded-full flex items-center justify-center font-bold text-xl">
                You
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full">{localVideoTag}</div>
            )}

            <div className="absolute text-white font-bold bg-gray-500 opacity-50 bottom-0 left-0 px-4 ">
              You
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoLayoutGrid;
